import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  Alert,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import DraftsIcon from "@mui/icons-material/Drafts";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

/**
 * DraftsModal Component
 * Shows saved drafts for a module.
 * Admin can:
 * - Continue editing
 * - Submit/publish
 * - Delete draft
 * Shows maximum 3 drafts warning.
 */
const DraftsModal = ({
  open,
  onClose,
  moduleName = "Module",
  drafts = [],
  onContinueEditing,
  onPublishDraft,
  onDeleteDraft,
  onClearAllDrafts,
}) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: isLight ? "#FFFFFF" : "linear-gradient(135deg, #13222f 0%, #1a2f3f 100%)",
          color: isLight ? "#0F172A" : "#FFFFFF",
          borderRadius: 3,
          border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(76, 161, 175, 0.3)",
          boxShadow: isLight ? "0 16px 40px rgba(15, 23, 42, 0.12)" : "0 16px 40px rgba(0, 0, 0, 0.5)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)",
          pb: 1.5,
        }}
      >
        <Box display="flex" alignItems="center" gap={1.2}>
          <DraftsIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF", fontSize: 24 }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
            Saved Drafts - {moduleName}
          </Typography>
          <Chip
            label={`${drafts.length} / 5`}
            size="small"
            sx={{
              bgcolor: drafts.length >= 5
                ? (isLight ? "#FEE2E2" : "rgba(239, 68, 68, 0.25)")
                : (isLight ? "#F0FDFA" : "rgba(76, 161, 175, 0.25)"),
              color: drafts.length >= 5
                ? (isLight ? "#DC2626" : "#EF4444")
                : (isLight ? "#0D9488" : "#64d2e2"),
              border: isLight
                ? (drafts.length >= 5 ? "1px solid #FECACA" : "1px solid #CCFBF1")
                : (drafts.length >= 5 ? "1px solid rgba(239, 68, 68, 0.5)" : "1px solid rgba(76, 161, 175, 0.5)"),
              fontWeight: 700,
              fontSize: "0.75rem",
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2.5 }}>
        {drafts.length >= 5 && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              bgcolor: isLight ? "#FFFBEB" : "rgba(245, 158, 11, 0.15)",
              color: isLight ? "#92400E" : "#FDE68A",
              border: isLight ? "1px solid #FDE68A" : "1px solid rgba(245, 158, 11, 0.35)",
              "& .MuiAlert-icon": { color: isLight ? "#D97706" : "#F59E0B" },
            }}
          >
            You have reached the maximum of 5 drafts. Please remove an existing draft before creating a new one.
          </Alert>
        )}

        {drafts.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <DraftsIcon sx={{ fontSize: 44, color: isLight ? "#CBD5E1" : "rgba(255, 255, 255, 0.2)", mb: 1 }} />
            <Typography variant="body1" sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.6)" }}>
              No drafts saved yet.
            </Typography>
            <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.4)", display: "block", mt: 0.5 }}>
              Unfinished content is automatically saved when leaving the form.
            </Typography>
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.8}>
            {drafts.map((draft, idx) => (
              <Card
                key={draft.id || idx}
                variant="outlined"
                sx={{
                  bgcolor: isLight ? "#F8FAFC" : "rgba(255, 255, 255, 0.04)",
                  border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: 2,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: isLight ? "#F1F5F9" : "rgba(255, 255, 255, 0.07)",
                    borderColor: isLight ? "#0D9488" : "rgba(76, 161, 175, 0.4)",
                  },
                }}
              >
                <CardContent sx={{ p: "16px !important" }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                        {draft.title || "Untitled Draft"}
                      </Typography>
                      {draft.summary && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isLight ? "#475569" : "rgba(255, 255, 255, 0.7)",
                            mt: 0.3,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            fontSize: "0.82rem",
                          }}
                        >
                          {draft.summary}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.45)", mt: 0.8, display: "block" }}>
                        Saved {dayjs(draft.updatedAt).fromNow()} ({dayjs(draft.updatedAt).format("MMM D, YYYY h:mm A")})
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={0.8} sx={{ flexShrink: 0 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                        onClick={() => {
                          if (onContinueEditing) onContinueEditing(draft);
                          onClose();
                        }}
                        sx={{
                          borderColor: isLight ? "#CBD5E1" : "rgba(76, 161, 175, 0.4)",
                          color: isLight ? "#0D9488" : "#64d2e2",
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          "&:hover": {
                            borderColor: isLight ? "#0D9488" : "#4CA1AF",
                            bgcolor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.15)",
                          },
                        }}
                      >
                        Edit
                      </Button>

                      {onPublishDraft && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<PublishIcon sx={{ fontSize: 15 }} />}
                          onClick={() => {
                            onPublishDraft(draft);
                            onClose();
                          }}
                          sx={{
                            bgcolor: isLight ? "#059669" : "rgba(16, 185, 129, 0.8)",
                            color: "#FFFFFF",
                            textTransform: "none",
                            fontSize: "0.75rem",
                            fontWeight: 600,
                            "&:hover": {
                              bgcolor: isLight ? "#047857" : "#10B981",
                            },
                          }}
                        >
                          Submit
                        </Button>
                      )}

                      <IconButton
                        size="small"
                        onClick={() => onDeleteDraft(draft.id)}
                        sx={{
                          color: isLight ? "#EF4444" : "rgba(239, 68, 68, 0.8)",
                          "&:hover": {
                            bgcolor: isLight ? "rgba(239, 68, 68, 0.08)" : "rgba(239, 68, 68, 0.15)",
                            color: "#DC2626",
                          },
                        }}
                        title="Delete Draft"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255, 255, 255, 0.08)", px: 3, py: 1.5, justifyContent: "space-between" }}>
        {onClearAllDrafts && drafts.length > 0 ? (
          <Button
            onClick={onClearAllDrafts}
            size="small"
            sx={{
              color: "#EF4444",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              "&:hover": { bgcolor: isLight ? "rgba(239, 68, 68, 0.08)" : "rgba(239, 68, 68, 0.12)" }
            }}
          >
            Clear All Drafts
          </Button>
        ) : <Box />}
        <Button onClick={onClose} sx={{ color: isLight ? "#64748B" : "rgba(255, 255, 255, 0.7)", textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DraftsModal;
